/**
 * Live data access. The fleet list is one query (`/api/live/units`), loaded once per scope;
 * realtime frames from the tab's WebSocket are merged into it in place (feed.ts). It is polled
 * only while the socket is down and re-fetched when the socket reconnects.
 */
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import type { components } from '@/lib/api/schema';

type S = components['schemas'];
export type VideoRequestBody = S['RequestBody'];
export type ActionOut = S['ActionOut'];

export const liveKeys = {
  all: ['live'] as const,
  units: (p: object) => ['live', 'units', p] as const,
  unit: (id: string) => ['live', 'unit', id] as const,
  track: (p: object) => ['live', 'track', p] as const,
  timeline: (p: object) => ['live', 'timeline', p] as const,
};

export interface LiveQuery {
  project?: string[];
  group?: string[];
}

/**
 * The fleet list, loaded once per scope; realtime frames keep it current (feed.ts). It is
 * polled only while the live connection is down, and re-fetched when it comes back.
 */
export function useLiveUnits(q: LiveQuery, socketOpen: boolean, enabled = true) {
  return useQuery({
    queryKey: liveKeys.units(q),
    // The map leaves group chips out (most of the payload); the focus panel's detail has them.
    queryFn: () => unwrap(api.GET('/api/live/units', { params: { query: { ...q, with_groups: false } } })),
    refetchInterval: socketOpen ? false : 15_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: !socketOpen,
    placeholderData: keepPreviousData,
    staleTime: socketOpen ? Infinity : 10_000,
    enabled,
  });
}

export function useLiveUnit(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: liveKeys.unit(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/live/units/{vehicle_id}', { params: { path: { vehicle_id: id ?? '' } } })),
    refetchInterval: 30_000,
    enabled: !!id && enabled,
  });
}

/** A window of one vehicle; without `end` it runs until now (and is refreshed every minute). */
export interface WindowQuery {
  vehicle: string;
  start: string;
  end?: string;
}

export function useTrack(q: WindowQuery | null, maxPoints = 3000, enabled = true) {
  return useQuery({
    queryKey: liveKeys.track({ ...q, maxPoints }),
    queryFn: () => unwrap(api.GET('/api/track', { params: { query: { vehicle: q?.vehicle ?? '', start: q?.start, end: q?.end, max_points: maxPoints } } })),
    enabled: !!q && enabled,
    refetchInterval: q && !q.end ? 60_000 : false,
  });
}

export function useTimeline(q: WindowQuery | null, enabled = true) {
  return useQuery({
    queryKey: liveKeys.timeline(q ?? {}),
    queryFn: () => unwrap(api.GET('/api/timeline', { params: { query: { vehicle: q?.vehicle ?? '', start: q?.start, end: q?.end } } })),
    enabled: !!q && enabled,
    refetchInterval: q && !q.end ? 60_000 : false,
  });
}

/** Ask the dash cam for footage (media module's action; its result message is shown). */
export function useRequestVideo() {
  return useMutation({
    mutationFn: async (body: VideoRequestBody) => (await unwrap(api.POST('/api/media/requests', { body }))) as unknown as ActionOut,
  });
}
